import React, { useRef } from 'react';
import {
  PropsSelectorUndefinedObject,
  propsStore,
  PropsStore,
} from './propsStore';
import {
  TProperties,
  TPropsComparator,
  TPropsConfiguration,
  TPropsSelector,
} from './types';
import { TId } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultComparator: TPropsComparator<any> = (a, b) => {
  return a === b;
};
function getDefaultSelector<Selected, PropsType = TProperties>() {
  return (current: PropsType) => {
    return current as unknown as Selected;
  };
}

export function isPropsConfigurationObject<
  Selected,
  PropsType extends Record<string, unknown> = TProperties,
>(
  value?:
    | TPropsSelector<Selected, PropsType>
    | TPropsConfiguration<Selected, PropsType>,
): value is TPropsConfiguration<Selected, PropsType> {
  return (
    typeof value === 'object' &&
    value &&
    ('selector' in value || 'comparator' in value || 'initialValue' in value)
  );
}

/**
 *
 * Este hook permite escuchar los cambios en las propiedades
 * de un campo en particular.
 *
 * @param configuration
 * Este objeto permite pasar las siguientes propiedades para ser
 * determinar el comportamiento del hook:
 *
 * **selector:** La función selectora es una función equivalente
 * a la que se utiliza en el useAppSelector de redux. Con ella
 * se puede seleccionar una porción de las props, evitando
 * re-renderizados innecesarios. Esto se debe a que el renderizado
 * solamente ocurrirá cuando la función selectora devuelva un valor
 * que sea distinto del anterior.
 *
 * El concepto de distinto es: si existe una función comparadora,
 * se determinará mediante la ejecución de dicha función, a la que
 * se le pasan como parámetros el objeto anterior y el nuevo.
 *
 * En caso de que no haya función comparadora, se comparará que
 * las props seleccionadas y las nuevas sean exactamente las mismas
 * (la referencia al mismo objeto).
 *
 * **comparator:** La función comparadora de la que estamos
 * hablando.
 *
 * **initialValue:** Un valor para setearlo al inicio, el mismo
 * solamente será seteado en caso de que ya no exista un valor
 * en el store.
 * @returns
 */
export function usePropsSelector<
  Selected = TProperties,
  PropsType extends Record<string, unknown> = TProperties,
>(
  fieldId: TId,
  configuration?: TPropsConfiguration<Selected, PropsType>,
): Selected;
/**
 *
 * Este hook permite escuchar los cambios en las propiedades
 * de un campo en particular.
 *
 * @param selector La función selectora es una función equivalente
 * a la que se utiliza en el useAppSelector de redux. Con ella
 * se puede seleccionar una porción de las props, evitando
 * re-renderizados innecesarios. Esto se debe a que el renderizado
 * solamente ocurrirá cuando la función selectora devuelva un valor
 * que sea distinto del anterior.
 *
 * El concepto de distinto es: si existe una función comparadora,
 * se determinará mediante la ejecución de dicha función, a la que
 * se le pasan como parámetros el objeto anterior y el nuevo.
 *
 * En caso de que no haya función comparadora, se comparará que
 * las props seleccionadas y las nuevas sean exactamente las mismas
 * (la referencia al mismo objeto).
 *
 * @param comparator La función comparadora de la que estamos
 * hablando.
 *
 * @returns
 */
export function usePropsSelector<
  Selected = TProperties,
  PropsType extends Record<string, unknown> = TProperties,
>(
  fieldId: TId,
  selector?:
    | TPropsSelector<Selected, PropsType>
    | TPropsConfiguration<Selected, PropsType>,
  comparator?: TPropsComparator<Selected>,
  anotherPropsStore?: PropsStore<PropsType>,
): Selected;
export function usePropsSelector<
  Selected = TProperties,
  PropsType extends Record<string, unknown> = TProperties,
>(
  fieldId: TId,
  par1?:
    | TPropsSelector<Selected, PropsType>
    | TPropsConfiguration<Selected, PropsType>,
  par2?: TPropsComparator<Selected>,
  par3?: PropsStore<PropsType>,
): Selected {
  const selector = React.useMemo(
    () => {
      return isPropsConfigurationObject(par1) ? par1.selector : par1;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const comparator = React.useMemo(
    () => {
      return isPropsConfigurationObject(par1) ? par1.comparator : par2;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialValue = React.useMemo(
    () => {
      return isPropsConfigurationObject(par1) ? par1.initialValue : undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const actualPropsStore = React.useMemo(
    () => {
      return (
        par3 ??
        (isPropsConfigurationObject(par1) ? par1.propsStore : propsStore) ??
        propsStore
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      par3 ??
        (isPropsConfigurationObject(par1) ? par1.propsStore : propsStore) ??
        propsStore,
    ],
  );

  const getCurrentProps = React.useCallback(() => {
    const currentProps = actualPropsStore.getFieldProps<PropsType>(fieldId);
    const willSetInitialValue =
      currentProps !== undefined && initialValue !== undefined;
    if (willSetInitialValue) {
      actualPropsStore.updateField(
        fieldId,
        initialValue as unknown as TProperties,
      );
    }
    return (selector ?? getDefaultSelector())(
      currentProps ?? (PropsSelectorUndefinedObject as PropsType),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [props, setProps] = React.useState<Selected>(getCurrentProps);

  const prevProps = useRef(props);
  const prevFieldId = useRef(fieldId);

  React.useEffect(() => {
    const unsuscribe = actualPropsStore.suscribe(
      fieldId,
      (newProps, isUrgent) => {
        const newSelectedProps = (selector ?? getDefaultSelector())(
          newProps as unknown as PropsType,
        );

        if (
          !(comparator ?? defaultComparator)(
            prevProps.current,
            newSelectedProps,
          ) ||
          prevFieldId.current !== fieldId
        ) {
          if (isUrgent) setProps(newSelectedProps);
          else
            React.startTransition(() => {
              setProps(newSelectedProps);
            });
        }
        prevProps.current = newSelectedProps;
      },
    );

    return () => {
      unsuscribe();
    };
  }, [fieldId, actualPropsStore, selector, comparator]);

  return props;
}
